'use client';

import Link from 'next/link';
import { useLocale } from '@/lib/i18n/LocaleContext';
import type { LocalizedJobPosting } from '@/lib/jobs';

export function CareerDetailClient({ job }: { job: LocalizedJobPosting }) {
  const { dict, href } = useLocale();
  const applyHref = href(`/apply?position=${job.slug}`);

  return (
    <div className="self-styled">
      {/* Hero */}
      <section
        className="relative flex flex-col items-center justify-center text-center"
        style={{ backgroundColor: '#173835', paddingTop: '140px', paddingBottom: '80px' }}
      >
        <Link
          href={href('/careers')}
          className="text-xs uppercase tracking-[0.28em] text-white/40 hover:text-white transition-colors"
          style={{ marginBottom: '24px' }}
        >
          <span className="rtl-mirror" aria-hidden="true">←</span> {dict.careers.job.backToAll}
        </Link>
        <span className="eyebrow" style={{ marginBottom: '16px' }}>
          {job.department} · {job.location}
        </span>
        <h1 className="rtl-display-l font-six-caps text-[calc(1rem+8vw)] leading-[0.9] text-white lowercase content-padding max-w-4xl">
          {job.title}
        </h1>
        <p className="text-white/50 text-sm font-mono uppercase tracking-[0.2em]" style={{ marginTop: '24px' }}>
          {job.employmentType}
          {job.openings > 1 ? (
            <>
              {' '}
              · <bdi dir="ltr">{job.openings}</bdi> {dict.careers.job.openings}
            </>
          ) : (
            ''
          )}
        </p>
        <p
          className="max-w-2xl px-8 text-center text-lg leading-relaxed text-white/60"
          style={{ marginTop: '32px' }}
        >
          {job.summary}
        </p>
        <Link
          href={applyHref}
          className="inline-flex items-center justify-center rounded-full border border-white bg-white text-base font-medium text-black transition-all duration-300 hover:bg-transparent hover:text-white"
          style={{ paddingLeft: '48px', paddingRight: '48px', paddingTop: '18px', paddingBottom: '18px', marginTop: '48px' }}
        >
          {dict.careers.job.applyNow}
        </Link>
      </section>

      {/* Details */}
      <section style={{ backgroundColor: '#0c0c0c', paddingTop: '100px', paddingBottom: '150px' }}>
        <div className="content-padding grid md:grid-cols-2" style={{ maxWidth: '1100px', margin: '0 auto', gap: '64px' }}>
          <div>
            <h2 className="text-white text-xl font-medium" style={{ marginBottom: '24px' }}>{dict.careers.job.whatYouWillDo}</h2>
            <ul className="text-white/60 leading-relaxed" style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {job.responsibilities.map((item) => (
                <li key={item} className="flex gap-3">
                  <span className="text-emerald-400">✦</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h2 className="text-white text-xl font-medium" style={{ marginBottom: '24px' }}>{dict.careers.job.whatWereLookingFor}</h2>
            <ul className="text-white/60 leading-relaxed" style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {job.requirements.map((item) => (
                <li key={item} className="flex gap-3">
                  <span className="text-emerald-400">✦</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="content-padding text-center" style={{ maxWidth: '1100px', margin: '0 auto', marginTop: '80px' }}>
          <Link
            href={applyHref}
            className="inline-flex items-center justify-center rounded-full border border-white bg-transparent text-base font-medium text-white transition-all duration-300 hover:bg-white hover:text-black"
            style={{ paddingLeft: '48px', paddingRight: '48px', paddingTop: '18px', paddingBottom: '18px', gap: '8px' }}
          >
            {dict.careers.job.applyForRole} <span className="rtl-mirror" aria-hidden="true">→</span>
          </Link>
        </div>
      </section>
    </div>
  );
}
